import React, { useState, useCallback, Suspense, lazy } from 'react';
import { useNavigate } from 'react-router-dom';
import debounce from 'lodash.debounce';
import './DashboardHeader.css';
import { sync_url } from '../UrlStore';

// 🔁 Memoized Sync Button
const SyncButton = React.memo(({ loading, onClick }) => (
  <button onClick={onClick} className="sync-button" disabled={loading}>
    {loading ? 'Syncing...' : 'Sync'}
  </button>
));

// 🔁 Memoized Search Button
const SearchButton = React.memo(({ onClick }) => (
  <button onClick={onClick}>Search</button>
));

// 🔁 Lazy-loaded Inline SyncOverlay Component
const LazySyncOverlay = lazy(() =>
  Promise.resolve({
    default: React.memo(() => (
      <div className="overlay">
        <div className="loader-container">
          <div className="spinner"></div>
          <p>Please wait, data is syncing...</p>
        </div>
      </div>
    )),
  })
);

function DashboardHeader({ onSearch }) {
  const navigate = useNavigate();
  const [nameSearch, setNameSearch] = useState('');
  const [yearSearch, setYearSearch] = useState('');
  const [loading, setLoading] = useState(false);

  // ⏳ Debounced Search
  const debouncedSearch = useCallback(
    debounce((filters) => {
      onSearch(filters);
    }, 300),
    [onSearch]
  );

  const handleSearch = useCallback(() => {
    debouncedSearch({ name: nameSearch, year: yearSearch });
  }, [nameSearch, yearSearch, debouncedSearch]);

  // 🔄 Sync Handler with Lazy Imports
  const handleSync = useCallback(async () => {
    setLoading(true);
    try {
      const [{ default: axios }, { toast }] = await Promise.all([
        import('axios'),
        import('react-toastify'),
      ]);

      const response = await axios.get(sync_url, { withCredentials: true });
      console.log('Sync response data:', response.data);

      navigate('/dashboard/productlist');
      toast.success('Data sync completed successfully!');
    } catch (err) {
      console.error('Sync error:', err);
      const { toast } = await import('react-toastify');
      toast.error(`Sync failed: ${err.response?.data?.message || err.message}`);
    } finally {
      setLoading(false);
    }
  }, [navigate]);

  return (
    <div className="header-container">
      <div className="search-container">
        <input
          type="text"
          placeholder="Search by name"
          value={nameSearch}
          onChange={(e) => setNameSearch(e.target.value)}
          className="search-input"
        />
        <input
          type="number"
          placeholder="Search by year"
          value={yearSearch}
          onChange={(e) => setYearSearch(e.target.value)}
          className="search-input"
        />
        <SearchButton onClick={handleSearch} />
      </div>

      <SyncButton loading={loading} onClick={handleSync} />

      {loading && (
        <Suspense fallback={null}>
          <LazySyncOverlay />
        </Suspense>
      )}
    </div>
  );
}

export default DashboardHeader;
